import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AlertThresholdConfig = ({ thresholds, onUpdate }) => {
  const [localThresholds, setLocalThresholds] = useState(thresholds);
  const [hasChanges, setHasChanges] = useState(false);

  const handleChange = (key, value) => {
    setLocalThresholds(prev => ({ ...prev, [key]: parseInt(value) }));
    setHasChanges(true);
  };

  const handleSave = () => {
    onUpdate(localThresholds);
    setHasChanges(false);
  };

  const handleReset = () => {
    setLocalThresholds(thresholds);
    setHasChanges(false);
  };

  const thresholdConfigs = [
    {
      key: 'signupVelocity',
      label: 'Signup Velocity Alert',
      description: 'Trigger alert when signups per hour exceed this threshold',
      min: 10,
      max: 200,
      step: 10,
      unit: 'signups/hour'
    },
    {
      key: 'conversionRate',
      label: 'Conversion Rate Alert',
      description: 'Trigger alert when conversion rate drops below this percentage',
      min: 1,
      max: 50,
      step: 1,
      unit: '%'
    },
    {
      key: 'featureUsageSpike',
      label: 'Feature Usage Spike',
      description: 'Trigger alert when feature usage exceeds this threshold',
      min: 50,
      max: 1000,
      step: 50,
      unit: 'requests'
    },
    {
      key: 'paymentFailureRate',
      label: 'Payment Failure Rate',
      description: 'Trigger alert when payment failures exceed this rate',
      min: 1,
      max: 20,
      step: 1,
      unit: '%'
    },
    {
      key: 'anomalyScore',
      label: 'Anomaly Severity Score',
      description: 'Trigger alert when anomaly severity exceeds this score',
      min: 1,
      max: 10,
      step: 1,
      unit: '/10'
    }
  ];

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="bg-muted/50 px-6 py-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon name="Sliders" size={20} className="text-primary" strokeWidth={2} />
            <h2 className="text-lg font-semibold text-foreground">Alert Thresholds</h2>
          </div>
          {hasChanges && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleReset}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Reset
              </button>
              <Button onClick={handleSave} size="sm" iconName="Save">
                Save
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="p-6 space-y-6">
        {thresholdConfigs?.map((config) => (
          <div key={config?.key} className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">
                {config?.label}
              </label>
              <span className="text-sm font-semibold text-primary">
                {localThresholds?.[config?.key]} {config?.unit}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">{config?.description}</p>
            <input
              type="range"
              min={config?.min}
              max={config?.max}
              step={config?.step}
              value={localThresholds?.[config?.key]}
              onChange={(e) => handleChange(config?.key, e?.target?.value)}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{config?.min} {config?.unit}</span>
              <span>{config?.max} {config?.unit}</span>
            </div>
          </div>
        ))}

        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 flex items-start gap-3">
          <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" strokeWidth={2} />
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-1">Threshold Configuration</h4>
            <p className="text-xs text-muted-foreground">
              Alerts are automatically triggered when metrics exceed configured thresholds. 
              Adjust these values based on your platform's normal operating ranges.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertThresholdConfig;